import axios from 'axios';
import React, {useState, useCallback, useEffect} from 'react';
import ReactPhoneInput from 'react-phone-input-2';
import {useAnalytics} from 'contexts/Analytics';
import {useLandingURL} from 'contexts/LandingURL';
import Checkmark from 'components/ui/icon/Checkmark';
import ArrowRight from 'components/ui/icon/ArrowRight';
import {useTariff} from 'contexts/Tariff';

import './RequestForm.scss';

export function formatPhoneNumber(phone) {
  /** Makes +7 (908) 349-35-51 from 79083493551 */
  return phone
    .replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5')
    .replace(/ /g, '\u00A0');
}

const RequestForm = ({
  slug,
  showTariffInput,
  showCommentInput,
  background = 'light',
}) => {
  const {landingURL} = useLandingURL();
  const [tariff, setTariff] = useState('');
  const [comment, setComment] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const tariffAPI = useTariff();
  const chosenSupplments = tariffAPI.getSupplements(slug);

  const commentInput = [
    `Тариф: ${tariff || slug}`,
    ...(tariffAPI.tariffState.squareMeters
      ? [`Метраж: ${tariffAPI.tariffState.squareMeters}`]
      : []),
    ...(comment ? [`\nКомментарий: ${comment}\n`] : []),
    `Выбранные услуги:\n${chosenSupplments
      .map(supplement => supplement.name)
      .join('\n')}`,
  ].join('\n');

  const send = useCallback(() => {
    setIsLoading(true);
    axios
      .post('https://home.hands.ru/api/v1/little-big/create-order/', {
        phone,
        comment: commentInput,
        landingURL,
      })
      .then(() => {
        setShowSuccess(true);
      })
      .catch(() => {
        setShowSuccess(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [phone, comment, tariff, chosenSupplments, landingURL]);

  if (showSuccess) {
    return (
      <div className={`request-form request-form_${background}-bg`}>
        <p className="request-form__success-text">
          Мы перезвоним вам на номер{' '}
          <span style={{whiteSpace: 'nowrap'}}>{formatPhoneNumber(phone)}</span>
        </p>
        <button
          className="request-form__resubmit"
          onClick={() => {
            setPhone('');
            setShowSuccess(false);
          }}
        >
          Отправить заявку повторно
          <ArrowRight color={'#00f'} />
        </button>
      </div>
    );
  }

  return (
    <div className={`request-form request-form_${background}-bg`}>
      {showTariffInput && <TariffInput tariff={tariff} onChange={setTariff} />}

      {showCommentInput && (
        <CommentInput onChange={e => setComment(e.target.value)} />
      )}

      <PhoneInput
        phone={phone}
        onChange={setPhone}
        onSend={send}
        isLoading={isLoading}
      />
    </div>
  );
};

const TariffInput = ({tariff, onChange}) => {
  return (
    <div className="request-form__tariff-input">
      <p className="request-form__tariff-input-description">
        Если вас интересует определённый тариф, отметьте его
      </p>

      <div className="request-form__tariff-input-items">
        {['Косметический', 'Капитальный', 'Дизайнерский', 'Индивидуальный'].map(
          currentTariff => {
            const isActive = tariff === currentTariff;

            return (
              <div
                className={`request-form__tariff-input-item ${
                  isActive ? 'request-form__tariff-input-item_active' : ''
                }`}
                onClick={() => onChange(currentTariff)}
                key={currentTariff}
              >
                {currentTariff}
                {isActive ? <Checkmark /> : null}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

const CommentInput = ({onChange}) => {
  return (
    <textarea
      className="request-form__comment-input"
      onChange={onChange}
      placeholder="Если нужно, опишите задачу"
    ></textarea>
  );
};

const PhoneInput = ({
  phone,
  onChange: onChangeFromProps,
  onSend,
  isLoading,
}) => {
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const {handleEvent} = useAnalytics();

  const onChange = useCallback(
    newValue => {
      if (newValue.length === 1 && newValue === '9') {
        onChangeFromProps('7' + newValue);
      } else {
        onChangeFromProps(newValue);
      }
    },
    [onChangeFromProps],
  );

  useEffect(() => {
    setIsPhoneValid(phone.length === 11);
  }, [phone]);

  return (
    <div className="request-form__phone-input">
      <ReactPhoneInput
        specialLabel=""
        inputProps={{
          id: 'phone',
          name: 'phone',
          placeholder: '+7 (000) 000-00-00',
          required: true,
        }}
        disabled={isLoading}
        value={phone}
        onChange={onChange}
      />

      <div className="request-form__phone-input-footer">
        <p className="request-form__phone-input-disclaimer">
          Вы&nbsp;соглашаетесь с&nbsp;
          <a
            href="https://hands.ru/documents/privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            Политикой конфиденциальности
          </a>{' '}
          и&nbsp;
          <a
            href="https://hands.ru/documents/terms-of-use-service-ruki/"
            target="_blank"
            rel="noreferrer"
          >
            Условиями использования Сервиса Руки
          </a>
        </p>

        <button
          className={`request-form__phone-input-submit ${
            isPhoneValid ? '' : 'request-form__phone-input-submit_disabled'
          }`}
          onClick={onSend}
          onClickCapture={handleEvent('send_request')}
          disabled={isLoading || !isPhoneValid}
        >
          Отправить заявку
          <ArrowRight />
        </button>
      </div>

      <p className="request-form__phone-input-tooltip">
        Оставьте ваш номер телефона. Мы&nbsp;перезвоним, обсудим вашу задачу,
        ответим на&nbsp;вопросы.
      </p>
    </div>
  );
};

export default RequestForm;
